import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Card,
  Group,
  Badge,
  Center,
  Box,
  Loader,
  Alert,
  Divider,
  Image,
} from '@mantine/core';
import {
  IconMail,
  IconCreditCard,
  IconCheck,
  IconAlertCircle,
  IconLogout,
} from '@tabler/icons-react';
import { useAuth } from '@/hooks/useAuth';
import { useMyInvites, useAcceptInvite } from '@/hooks/api/useOrganizationMembers';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import { useOrgStore } from '@/stores/orgStore';

export function NoOrganizationPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: invites, isLoading: invitesLoading } = useMyInvites(user?.email || '');
  const acceptInvite = useAcceptInvite();
  const { setOrganization } = useOrgStore();

  const handleAcceptInvite = async (token: string) => {
    try {
      const result = await acceptInvite.mutateAsync(token);

      // Fetch the new organization
      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', result.organization_id)
        .single();

      if (org) {
        setOrganization(org, result);
      }

      notifications.show({
        title: 'Welcome!',
        message: 'You have successfully joined the organization',
        color: 'green',
      });

      navigate('/dashboard');
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to accept invite',
        color: 'red',
      });
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={600}>
        <Center mb="xl">
          <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p="xl" radius="md">
          <Stack gap="lg">
            <div>
              <Title order={2}>Welcome, {user?.email?.split('@')[0]}!</Title>
              <Text c="dimmed" size="sm" mt="xs">
                You're not part of any organization yet. Join an existing organization or create a new one.
              </Text>
            </div>

            {/* Pending Invites Section */}
            {invitesLoading ? (
              <Center py="lg">
                <Loader size="sm" />
              </Center>
            ) : invites && invites.length > 0 ? (
              <div>
                <Group gap="xs" mb="sm">
                  <IconMail size={20} />
                  <Text fw={600}>Pending Invitations</Text>
                  <Badge size="sm" color="blue">{invites.length}</Badge>
                </Group>

                <Stack gap="sm">
                  {invites.map((invite) => (
                    <Card key={invite.id} withBorder padding="md" radius="md">
                      <Group justify="space-between" wrap="nowrap">
                        <div>
                          <Text fw={600}>{invite.organization?.name || 'Organization'}</Text>
                          <Text size="sm" c="dimmed">
                            Role: {invite.is_admin ? 'Admin' : invite.role || 'Member'}
                          </Text>
                        </div>
                        <Button
                          size="sm"
                          leftSection={<IconCheck size={16} />}
                          onClick={() => handleAcceptInvite(invite.token)}
                          loading={acceptInvite.isPending}
                        >
                          Join
                        </Button>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </div>
            ) : (
              <Alert icon={<IconAlertCircle size={16} />} color="gray" variant="light">
                No pending invitations. Ask an organization admin to invite you, or start a free trial below.
              </Alert>
            )}

            <Divider label="or" labelPosition="center" />

            {/* Subscribe Section */}
            <Button
              component={Link}
              to="/pricing"
              variant="light"
              size="lg"
              leftSection={<IconCreditCard size={20} />}
              fullWidth
            >
              Start 30-Day Free Trial
            </Button>
            <Text size="xs" c="dimmed" ta="center">
              Choose a plan to create your organization
            </Text>

            <Divider />

            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconLogout size={16} />}
              onClick={handleLogout}
            >
              Sign out
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
